import React, { useState, useEffect, useRef } from "react";
import { RadioButton } from "primereact/radiobutton";
import PropTypes from "prop-types";
import SimpleModal from "./SimpleModal";
import ForgotPasswordModal from "./ForgotPasswordModal";
import ChangePasswordModal from "./ChangePasswordModal";
import ComplianceModal from "./ComplianceModal";
import PasswordInput from "./PasswordInput";
import { useToast } from "../ToastProvider";
import { useAuthStore } from "../../store/authStore";
import { router } from "../../data/Services/ordersRouter";
import {
  isComplianceAcceptedToday,
  markComplianceAccepted,
} from "../../utils/complianceService";

const RouterLoginModal = ({ visible, onHide, onSuccessfulSubmit }) => {
  const { showSuccess, showError, showWarning } = useToast();
  const { getUsername, getBroker } = useAuthStore();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCompliance, setShowCompliance] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [accountType, setAccountType] = useState("demo"); // 'real' ou 'demo'
  const passwordInputRef = useRef(null);
  const pendingLoginDataRef = useRef(null); // Store login data for compliance flow

  // Clear password and auto-focus password input when modal opens
  useEffect(() => {
    if (visible) {
      // Clear password field when modal opens
      setPassword("");

      // Use setTimeout to ensure modal is fully rendered
      const timer = setTimeout(() => {
        // Try multiple approaches to focus the password input
        const passwordElement = document.getElementById("password");
        if (passwordElement) {
          passwordElement.focus();
        } else if (passwordInputRef.current) {
          // If ref exists, try to focus the input element inside
          const inputElement =
            passwordInputRef.current.getInput?.() ||
            passwordInputRef.current.querySelector?.("input") ||
            passwordInputRef.current;
          if (inputElement && inputElement.focus) {
            inputElement.focus();
          }
        }
      }, 200); // Increased delay to ensure modal animation completes

      return () => clearTimeout(timer);
    }
  }, [visible]);

  // Force simulator mode for non-numeric logins (only numeric accounts can use real mode)
  useEffect(() => {
    const username = getUsername();
    const isNonNumeric = username && !/^\d+$/.test(username);
    if (isNonNumeric && accountType === "real") {
      setAccountType("demo");
    }
  }, [accountType]);

  // Handle Enter key press for default submit behavior
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" && visible && !isLoading) {
        event.preventDefault();
        handleSubmit();
      }
    };

    if (visible) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible, isLoading, password, accountType]); // Dependencies to ensure handleSubmit has current values

  // Listen for automatic password expiration and blocked access events
  useEffect(() => {
    const handlePasswordExpired = (event) => {
      console.log(
        "🔐 RouterLoginModal: Password expired event received",
        event.detail,
      );

      // Close the login modal first
      if (onHide) onHide();

      // Open the change password modal after a short delay
      setTimeout(() => {
        setShowChangePassword(true);
      }, 300); // Delay to allow login modal to close smoothly
    };

    const handleAccessBlocked = (event) => {
      console.log(
        "🚫 RouterLoginModal: Access blocked event received",
        event.detail,
      );

      // Close the login modal first
      if (onHide) onHide();

      // Open the forgot password modal after a short delay
      setTimeout(() => {
        setShowForgotPassword(true);
      }, 300); // Delay to allow login modal to close smoothly
    };

    // Add event listeners
    window.addEventListener("routerPasswordExpired", handlePasswordExpired);
    window.addEventListener("routerAccessBlocked", handleAccessBlocked);

    // Cleanup on unmount
    return () => {
      window.removeEventListener(
        "routerPasswordExpired",
        handlePasswordExpired,
      );
      window.removeEventListener("routerAccessBlocked", handleAccessBlocked);
    };
  }, [onHide]); // Re-register if onHide changes

  const handleSubmit = async () => {
    if (!password.trim()) {
      showError("Por favor, digite sua senha", "Campo obrigatório");
      return;
    }

    const username = getUsername();
    const broker = getBroker();

    if (!username) {
      showError(
        "Usuário não encontrado. Faça login no MarketData primeiro.",
        "Erro de autenticação",
      );
      return;
    }

    if (!broker) {
      showError(
        "Corretora não encontrada. Faça login no MarketData primeiro.",
        "Erro de autenticação",
      );
      return;
    }

    setIsLoading(true);

    try {
      // Determine the broker to use for login
      const loginBroker = accountType === "demo" ? "SIMULATOR" : broker;

      console.log(
        `🔐 Attempting router login for user: ${username}, broker: ${loginBroker}, account: ${accountType}`,
      );

      const result = await router.login(loginBroker, username, password);

      if (result === "OK" || result === undefined) {
        // Login successful
        console.log(
          `✅ Router login successful for ${username}@${broker} (${accountType})`,
        );

        // Check if compliance needs to be shown (ONLY for real accounts)
        if (accountType === "real" && !isComplianceAcceptedToday()) {
          console.log(
            "📋 Compliance not accepted today for real account, showing modal...",
          );
          // Store login data for compliance flow
          pendingLoginDataRef.current = { username, broker, accountType };
          // Hide login modal and show compliance
          if (onHide) onHide();
          setShowCompliance(true);
        } else {
          // Compliance not needed (simulator) or already accepted today (real), complete login
          if (accountType === "demo") {
            console.log("✅ Simulator account - compliance not required");
          } else {
            console.log("✅ Real account - compliance already accepted today");
          }

          showSuccess(
            `Login no roteador realizado com sucesso! (${accountType === "real" ? "Conta Real" : "Simulador"})`,
            "Sucesso",
          );

          if (onSuccessfulSubmit) onSuccessfulSubmit();
          if (onHide) onHide();

          // Clear form
          setPassword("");
          setAccountType("demo");
        }
      } else {
        // Login failed with message
        // Skip showing error toast for "Senha Expirada" and "Acesso bloqueado"
        // because ordersRouter already showed a specific toast and will trigger the appropriate modal
        const decodedResult = decodeURIComponent(result || "");
        if (
          !decodedResult.includes("Senha Expirada") &&
          !decodedResult.includes("Acesso bloqueado")
        ) {
          showError(result || "Falha no login do roteador", "Erro de login");
        }
        console.error(`❌ Router login failed: ${result}`);
      }
    } catch (error) {
      console.error("Router login error:", error);
      showError(
        error.message || "Erro inesperado no login do roteador",
        "Erro",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const currentUsername = getUsername();
  const currentBroker = getBroker();

  // Check if username is NOT numeric (only numeric accounts can use real mode)
  const isNonNumericLogin = currentUsername && !/^\d+$/.test(currentUsername);

  // Broker names mapping
  const getBrokerName = (brokerCode) => {
    const brokerNames = {
      XPI: "XP Investimentos",
      CLEAR: "Clear Corretora",
      TZ: "TradeZone",
      RICO: "Rico Investimentos",
      SIMULATOR: "Simulador",
    };
    return brokerNames[brokerCode] || brokerCode;
  };

  // Compliance handlers
  const handleComplianceAccept = async () => {
    console.log("✅ Compliance accepted");
    markComplianceAccepted();
    setShowCompliance(false);

    const loginData = pendingLoginDataRef.current;
    if (loginData) {
      showSuccess(
        `Login no roteador realizado com sucesso! (${loginData.accountType === "real" ? "Conta Real" : "Simulador"})`,
        "Sucesso",
      );

      if (onSuccessfulSubmit) onSuccessfulSubmit();

      // Clear form
      setPassword("");
      setAccountType("demo");
      pendingLoginDataRef.current = null;
    }
  };

  const handleComplianceReject = async () => {
    console.log("❌ Compliance rejected - logging out from router");
    setShowCompliance(false);

    // Logout from router since compliance was not accepted
    try {
      router.stop();
      console.log("🛑 Router disconnected after compliance rejection");
      showWarning(
        "Você deve aceitar o aviso de compliance para usar o roteador.",
        "Aviso Não Aceito",
      );
    } catch (error) {
      console.error("Error stopping router after compliance rejection:", error);
      showWarning(
        "Você deve aceitar o aviso de compliance para usar o roteador.",
        "Aviso Não Aceito",
      );
    }

    // Clear pending data
    pendingLoginDataRef.current = null;
    setPassword("");
    setAccountType("demo");
  };

  return (
    <>
      <SimpleModal
        title="Roteamento de Ordens"
        visible={visible}
        onHide={onHide}
        onConfirm={handleSubmit}
        confirmLabel={isLoading ? "Conectando..." : "Conectar"}
        showCancel={!isLoading}
        size="small"
        variant="form"
        className="router-modal"
      >
        <div className="router-login-form">
          {/* User info */}
          <div className="info-box">
            <div>
              Usuário: <strong>{currentUsername || "Não logado"}</strong>
            </div>
            <div>
              Corretora:{" "}
              <strong>{getBrokerName(currentBroker) || "Não definida"}</strong>
            </div>
          </div>

          {/* Account type */}
          <div className="form-group">
            <label>Tipo de Conta:</label>
            <div className="radio-group">
              <div className="radio-option">
                <RadioButton
                  inputId="demo"
                  name="accountType"
                  value="demo"
                  onChange={(e) => setAccountType(e.value)}
                  checked={accountType === "demo"}
                  disabled={isLoading}
                />
                <label htmlFor="demo">Simulador</label>
              </div>
              <div className="radio-option">
                <RadioButton
                  inputId="real"
                  name="accountType"
                  value="real"
                  onChange={(e) => setAccountType(e.value)}
                  checked={accountType === "real"}
                  disabled={isLoading || isNonNumericLogin}
                />
                <label
                  htmlFor="real"
                  style={{ opacity: isNonNumericLogin ? 0.5 : 1 }}
                >
                  Conta Real
                </label>
              </div>
            </div>
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password">Senha de Roteamento</label>
            <PasswordInput
              ref={passwordInputRef}
              inputId="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              tabIndex={1}
              disabled={isLoading}
              autoFocus={visible && !isLoading}
            />
          </div>

          {/* Links */}
          <div className="form-links">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              data-evo-id="routerloginmodal-btn-forgot-password"
            >
              Esqueci minha senha!
            </button>
            <button
              type="button"
              onClick={() => setShowChangePassword(true)}
              data-evo-id="routerloginmodal-btn-change-password"
            >
              Alterar minha senha!
            </button>
          </div>
        </div>
      </SimpleModal>
      <ForgotPasswordModal
        visible={showForgotPassword}
        onHide={() => setShowForgotPassword(false)}
      />
      <ChangePasswordModal
        visible={showChangePassword}
        onHide={() => setShowChangePassword(false)}
      />
      <ComplianceModal
        visible={showCompliance}
        onAccept={handleComplianceAccept}
        onReject={handleComplianceReject}
      />
    </>
  );
};

RouterLoginModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccessfulSubmit: PropTypes.func,
};

export default RouterLoginModal;
